/**
 * ChatDealCreationCard — inline deal creation, triggered by a
 * request_deal_creation tool call (the `deal_creation_prompt` SSE event, NOT a
 * tool_result — driven by `store.dealCreationPrompt !== null`, rendered below the
 * assistant message).
 *
 * Thin wrapper around the shared DealWizardCard (variant="chat"): the chat asks
 * the SAME question set as the dashboard/modal create surfaces — one source of
 * truth, no drift. Model-extracted intent seeds the wizard via `prefill`. On
 * create it shows the inline SuccessState and injects a synthetic user message
 * that resumes the conversation with the new deal's context. Industry is not
 * asked (like the other surfaces) — the server resolves it from the account.
 *
 * Brand book: no rounded corners, semantic Tailwind tokens (text-success,
 * text-orange, surface-card, border-border), Feather icons, sentence case copy,
 * no emojis, IBM Plex inherited from parent.
 */
import { useState } from 'react';
import { CheckCircle } from 'react-feather';
import { Link } from 'react-router-dom';

import type { Deal, FinancingPurpose, TimelineUrgency } from '../../../api/types';
import { useChatStore } from '../../../stores/chatStore';
import { useChatStream } from '../hooks/useChatStream';
import { DealWizardCard, type WizardPrefill } from '../../deals/components/DealWizardCard';

interface SuccessStateProps {
    dealId: number;
    dealName: string;
}

function SuccessState({ dealId, dealName }: SuccessStateProps) {
    return (
        <div className="flex items-start gap-2 border border-success/40 bg-success/5 px-3 py-2 text-body-sm">
            <CheckCircle size={16} className="mt-0.5 shrink-0 text-success" strokeWidth={2.5} />
            <div className="min-w-0 flex-1">
                <p className="font-semibold text-txt-primary">Deal created</p>
                <p className="mt-1 truncate text-txt-secondary">{dealName}</p>
                <Link
                    to={`/deals/${dealId}`}
                    className="mt-1 inline-block text-caption text-orange hover:underline"
                >
                    View deal #{dealId} →
                </Link>
            </div>
        </div>
    );
}

export function ChatDealCreationCard() {
    const prompt = useChatStore(s => s.dealCreationPrompt);
    const clearDealCreationPrompt = useChatStore(s => s.clearDealCreationPrompt);
    const { send } = useChatStream();

    const [createdDeal, setCreatedDeal] = useState<{ id: number; name: string } | null>(null);

    // V1.4.1 — guard order matters. The SuccessState check must come before the
    // prompt check, otherwise the card unmounts the moment send()'s preamble
    // clears the prompt — and the user never sees the success confirmation.
    if (createdDeal) {
        return <SuccessState dealId={createdDeal.id} dealName={createdDeal.name} />;
    }
    if (!prompt) {
        return null;
    }

    const p = prompt.prefill;
    // prefill fields are loosely typed (string) on the SSE event; the wizard wants
    // the branded unions. Falsy → undefined so a blank/absent value stays unset.
    const prefill: WizardPrefill = {
        purpose: (p.financing_purpose || undefined) as FinancingPurpose | undefined,
        amount: p.financing_amount ?? undefined,
        urgency: (p.timeline_urgency || undefined) as TimelineUrgency | undefined,
        dealName: p.deal_name || undefined,
    };

    const handleCreated = (deal: Deal) => {
        setCreatedDeal({ id: deal.id, name: deal.deal_name });

        // Point the chat's active deal at the new deal so the upcoming synthetic
        // POST /chat carries the new deal_id, not a stale id from localStorage.
        useChatStore.getState().setDealContext({ deal_id: deal.id, deal_name: deal.deal_name });

        const industryPart = deal.industry ? `, industry: ${deal.industry}` : '';
        const syntheticText =
            `Deal created: "${deal.deal_name}" (deal_id: ${deal.id}, ` +
            `financing_purpose: ${deal.financing_purpose}, ` +
            `financing_amount: ${deal.financing_amount}${industryPart}). ` +
            `Please confirm and suggest next steps.`;

        // Brief delay so SuccessState paints before the next agent turn shifts the
        // layout. send()'s preamble clears dealCreationPrompt — no manual clear.
        window.setTimeout(() => {
            send({
                text: syntheticText,
                synthetic: true,
                // Authoritative deal context — the backend uses this id even if
                // dealContext (set just above) drifts before the POST.
                syntheticAction: { kind: 'deal_created', deal_id: deal.id },
            });
        }, 400);
    };

    return (
        <DealWizardCard
            variant="chat"
            prefill={prefill}
            onCancel={() => clearDealCreationPrompt()}
            onCreated={handleCreated}
        />
    );
}
